import React, { useEffect, useRef, useState } from 'react';
import { XAxisContextProvider } from 'utils/components/TimeSeriesChart/XAxisContext';
import {
    calculateTimeDomain,
    convertToMilliseconds,
} from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import IntraDayProfileChart from './chart/IntraDayProfileChart';
import { computeChartData } from './chart/computeChartData';
import IntraDayProfileHeader from './header/IntraDayProfileHeader';

import './IntraDayProfile.scss';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { buildIntraDayProfileStreamSlice } from 'widgets/intraDayProfile/store/data/streamSlice';
import { useIntraDayProfileConfigSlice } from 'widgets/intraDayProfile/store/configuration/configSlice';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useSelectedSymbolExternalMarketData } from 'marketData/hooks';
import { usePortfolioSelectedSymbolQuantities } from 'portfolio/store/hooks';

const {
    selectors: { useSelectStreamData },
} = buildIntraDayProfileStreamSlice();

export default function IntraDayProfile() {
    const { widgetKey, width, height } = useWidgetContext();

    useWidgetStream();

    const selectedSymbols = usePortfolioSelectedSymbolQuantities();

    const { config } = useIntraDayProfileConfigSlice();

    const chartWidth = width;

    const divRef = useRef<HTMLDivElement>(null);
    const [left, setLeft] = useState(0);
    useEffect(() => {
        if (!divRef.current) {
            return;
        }
        setLeft(divRef.current.getBoundingClientRect().left);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, divRef.current]);

    const dataState = useSelectStreamData();
    const { bucketSize } = config;

    const {
        barData: dapiBarData,
        refData: dapiRefData,
        messages: marketBarDataMessages,
    } = useSelectedSymbolExternalMarketData(bucketSize);

    const { breaksInTrading, dataSeries, rangeLabels, timeLineLabels } =
        computeChartData(dataState, bucketSize, dapiBarData, dapiRefData);
    const timeDomain = calculateTimeDomain(dataSeries);
    const timeLinePadding = convertToMilliseconds(bucketSize) / 2;
    const startTime =
        timeDomain[0] === 0 ? timeDomain[0] : timeDomain[0] - timeLinePadding;
    const endTime = timeDomain[1] + timeLinePadding;

    return (
        <XAxisContextProvider
            timeSeriesChartWidth={chartWidth}
            startTime={startTime}
            endTime={endTime}
            breaks={breaksInTrading}
            rangeLabels={rangeLabels}
            timeLineLabels={timeLineLabels}
        >
            <div className="IntraDayProfile" data-widget-id={widgetKey}>
                <div className="IntraDayProfileContainer" ref={divRef}>
                    <IntraDayProfileHeader
                        widgetHeight={height}
                        dataSeries={dataSeries}
                        bucketSize={bucketSize}
                        containerLeft={left}
                        messages={marketBarDataMessages}
                    />
                    {selectedSymbols.length > 0 && (
                        <IntraDayProfileChart
                            width={chartWidth}
                            dataSeries={dataSeries}
                        />
                    )}
                </div>
            </div>
        </XAxisContextProvider>
    );
}
